import { useGlobalState } from '@libs/client'
import Footer from '@ui/components/shared/Footer'
import Header from '@ui/components/shared/Header'
import OrderVariantsTracking from '@ui/components/trackings/OrderVariantsTracking'
import CustomerInfoOrdTracking from '@ui/components/trackings/CustomerInfoOrdTracking'
import { getLocalObject, iconPath, orderShopHelpMapping, setLocalObject } from '@ui/helpers'
import { useResize, useServerMounted, useSetting, useVersionCookie } from '@ui/hooks'
import { ShopHelpLocalKey, useOrderService } from '@ui/services'
import { useContext, useEffect, useMemo, useState } from 'react'
import css from 'styled-jsx/css'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import { LocalContact } from '@ui/modules'
import { PackageInfoTracking } from '@ui/components/trackings/PackageInfoTracking'
import { useAuthService } from '@ui/services/auth/auth.service'
import { LoginResponse } from '@ui/types/interfaces'
import Loading from '@ui/components/shared/Loading'
import { OrderContext } from '@ui/contexts'
import { useRedirect } from '@ui/hooks'
import { getDisplayNumberWithUnit, getTotalOrderLineItems } from '@ui/helpers/packageTracking'
import { PackageTrackingStatus } from '@ui/components/order/enum'
import OrderTrackingSale from '@ui/components/cross-sell/OrderTrackingSale'
import { TitleSale } from '@ui/components/cross-sell/OrderProcessing'

const TrackingsResult = () => {
  const AUTH_LOGIN = 'AUTH_LOGIN'
  const LOCAL_LIST_ORDER = 'LOCAL_LIST_ORDER'

  const router = useRouter()
  const redirect = useRedirect()
  const [sellpageSetting] = useSetting('sellpage')
  const { dispatchOrder } = useContext(OrderContext)
  const [, setContactForm] = useGlobalState<any>(ShopHelpLocalKey.CONTACT_FORM)
  const [orderDetail, setOrderDetail] = useGlobalState<Types.OrderShopHelp>(
    ShopHelpLocalKey.ORDER_DETAIL
  )

  const [cCollapsible, setcCollapsible] = useState<boolean | undefined>(false)
  const [cCollapsed, setcCollapsed] = useState<boolean | undefined>(true)
  const [isLoadingFull, setIsLoadingFull] = useState(false)
  const [isNotFound, setIsNotFound] = useState(false)
  const [isDataLoaded, setDataLoaded] = useState<boolean>(false)

  const { login } = useAuthService(getConfig().publicRuntimeConfig.shophelpBasePathApi)
  const { relative } = useOrderService(getConfig().publicRuntimeConfig.shophelpBasePathApi)
  const { detailByInfoAuth } = useOrderService(getConfig().publicRuntimeConfig.shophelpBasePathApi)

  const order = useMemo(() => orderShopHelpMapping(orderDetail ?? {}), [orderDetail])

  const getDisplayNumberPackage = getDisplayNumberWithUnit(
    (orderDetail?.parcel_infos ?? []).length,
    'package'
  )
  const totalOrderLineItems = getTotalOrderLineItems(orderDetail?.parcel_infos ?? [])
  const totalItems = totalOrderLineItems
    ?.map((o) => o?.quantity ?? 0)
    .reduce((prev, curr) => prev + curr, 0)
  const getDisplayNumberItems = getDisplayNumberWithUnit(totalItems, 'item')
  const parcelsQuantity = orderDetail?.parcel_infos ? orderDetail?.parcel_infos.length : 0

  const versionCookie = useVersionCookie()

  function toogleCollape() {
    setcCollapsed(!cCollapsed)
  }

  function handleResize() {
    if (process.browser) {
      const windowWidth = window.innerWidth
      if (windowWidth > 768) {
        setcCollapsible(false)
        setcCollapsed(false)
      } else {
        setcCollapsible(true)
      }
    }
  }

  async function getListOrder() {
    try {
      await relative({
        email: '',
        page: 1,
        size: 100
      }).then((res: Types.OrderShopHelp[]) => {
        setLocalObject(LOCAL_LIST_ORDER, res)
      })
    } catch (e) {
    } finally {
    }
  }

  async function fetchOrder(body: Types.LoginModel) {
    try {
      setContactForm({
        ...body
      })
      return await detailByInfoAuth(body).then((res: Types.OrderShopHelp) => {
        if (!res) {
          setIsNotFound(true)
        } else {
          setIsNotFound(false)
          setOrderDetail(res)
          dispatchOrder({
            type: 'REPLACE',
            payload: res
          })
          getListOrder()
        }
      })
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoadingFull(false)
    }
  }

  async function newFetchOrder(body: Types.LoginModel) {
    try {
      const localAuth = getLocalObject(AUTH_LOGIN)
      let result = true
      if (!localAuth || !localAuth.access_token) {
        result = await login(body).then((data: LoginResponse) => {
          if (data && (data.email || data.phone) && data.access_token) {
            return true
          }
          return false
        })
      }
      if (result) {
        setContactForm({
          ...body
        })
        return await detailByInfoAuth(body).then((res: Types.OrderShopHelp) => {
          if (!res) {
            setIsNotFound(true)
          } else {
            //
            setIsNotFound(false)
            setOrderDetail(res)
            dispatchOrder({
              type: 'REPLACE',
              payload: res
            })
            getListOrder()
          }
        })
      } else {
        setIsNotFound(false)
      }
    } catch (err: any) {
      console.log(err)
    } finally {
      setIsLoadingFull(false)
    }
  }

  const redirectToSelectFlow = (route: string) => {
    redirect(route, { routerOptions: { scroll: false }, query: { ref: 'order-tracking' } })
  }

  const listPackageStatus = useMemo(() => {
    return orderDetail?.parcel_infos?.map((o) => o?.status ?? '') ?? []
  }, [orderDetail?.parcel_infos])

  const isAllPackageCancelled = useMemo(() => {
    if (listPackageStatus.length <= 0) return true

    return listPackageStatus?.every((o) => o == PackageTrackingStatus.CANCELLED)
  }, [listPackageStatus])

  const isAnyPackageHasTN = useMemo(() => {
    //TN = Tracking Number
    const listTnStatus = [
      PackageTrackingStatus.ON_DELIVERY,
      PackageTrackingStatus.IN_DELAY,
      PackageTrackingStatus.UNDELIVERABLE,
      PackageTrackingStatus.DELIVERED
    ]

    return listPackageStatus?.some((o) => listTnStatus.includes(o as PackageTrackingStatus))
  }, [listPackageStatus])

  useResize(() => handleResize())

  useEffect(() => {
    setIsNotFound(false)
    setIsLoadingFull(true)
    handleResize()
    const query = router.query
    if (orderDetail) {
      if (query && (query.email || query.order)) {
        //via mail
        const body: Types.LoginModel = {
          email: query.email as string,
          order: query.order as string
        }
        newFetchOrder(body).then()
      } else {
        setIsNotFound(true)
        setIsLoadingFull(false)
      }
    } else {
      const localContactForm = getLocalObject(LocalContact.contactKey)
      if (query && (query.email || query.order)) {
        //via mail
        const body: Types.LoginModel = {
          email: query.email as string,
          order: query.order as string
        }
        newFetchOrder(body).then()
      } else if (
        (localContactForm?.email || localContactForm?.phone) &&
        (localContactForm?.order_name || localContactForm?.order)
      ) {
        const localAuth = getLocalObject(AUTH_LOGIN)
        const body: Types.LoginModel = localContactForm?.email
          ? {
              email: localContactForm.email,
              order: localContactForm.order_name || localContactForm.order
            }
          : {
              phone: localContactForm.phone,
              order: localContactForm.order_name || localContactForm.order
            }
        if (
          localAuth.phone == localContactForm.phone ||
          localAuth.email == localContactForm.email
        ) {
          fetchOrder(body).then()
        } else {
          newFetchOrder(body).then()
        }
      } else {
        setIsNotFound(false)
        setIsLoadingFull(false)
      }
    }
  }, [])

  useEffect(() => {
    // add check data loaded.
    const orderCodeCheck =
      document.querySelector('#ord-tracking-code-num')?.textContent?.split('#') ?? []
    if (orderCodeCheck.length == 2 && orderCodeCheck[1]) setDataLoaded(true)
  }, [orderDetail ?? {}])

  const isServerMounted = useServerMounted()

  return !isServerMounted ? (
    <div className="page tracking-order-page">
      <div hidden id="gtm_is_loaded">
        {isDataLoaded ? 'true' : 'false'}
      </div>
      {isLoadingFull && <Loading isFullWidth />}
      <div className="page__content-wrapper container is-fullwidth has-background-white-bis">
        {orderDetail && !isNotFound ? (
          <>
            <Header />
            <div className="page__content columns">
              <div className="left-column column p-8-tablet">
                <div className="order">
                  <OrderVariantsTracking
                    order={order}
                    title={orderDetail.title ?? sellpageSetting?.settings.general.title ?? ''}
                    orderShopHelp={orderDetail}
                  />
                  <div className="customer">
                    {!cCollapsible && <p className="customer__title">Customer Information</p>}
                    {cCollapsible && (
                      <div className="customer__title--collapse" onClick={toogleCollape}>
                        <figure className="title__icon image">
                          <img src={iconPath('account.svg')} alt="" />
                        </figure>
                        <span className="title__text">
                          {!cCollapsed ? 'Hide' : 'Show'} customer information
                        </span>
                        <figure className="title__icon--right image">
                          <img
                            src={iconPath('chevron_down.svg')}
                            alt=""
                            style={!cCollapsed ? { display: 'none' } : {}}
                          />
                          <img
                            src={iconPath('chevron_up.svg')}
                            alt=""
                            style={cCollapsed ? { display: 'none' } : {}}
                          />
                        </figure>
                      </div>
                    )}
                    {(!cCollapsed || !cCollapsible) && (
                      <CustomerInfoOrdTracking
                        paymentMethod={order.transactionProvider ?? 'PAYPAL'}
                        shippingAddress={order.shipping}
                        billingAddress={order.billing}
                        merchant_name={orderDetail.payment?.payment_merchant_name}
                        isLoading={false}
                        cardInfo={order.cardInfo}
                        customerEmail={orderDetail.customer_info?.email ?? ''}
                      />
                    )}
                    {cCollapsible && !cCollapsed && <hr />}
                  </div>
                </div>
              </div>
              <div className="column p-8-tablet has-background-white">
                <div className="tracking">
                  <div className="is-size-5 has-text-weight-bold mb-5 tracking__header">
                    <img src={iconPath('check.svg')} alt="check" />
                    <p id="ord-tracking-code-num" className="ml-3 mt-1">
                      Order {order.code}
                    </p>
                  </div>

                  {!isAllPackageCancelled &&
                    (isAnyPackageHasTN ? (
                      <div className="mb-2">
                        <span>
                          This order is shipped in <strong> {getDisplayNumberPackage} </strong>
                        </span>
                      </div>
                    ) : (
                      <div className="mb-2">
                        <span>Your order will be shipped out soon</span>
                      </div>
                    ))}

                  {/* total items */}
                  {orderDetail?.is_quantity_contract && !isAllPackageCancelled && (
                    <div className="quantity-total mb-3 mt-2">
                      <span className="quantity-wrapper">
                        Total number of items: <strong> {getDisplayNumberItems} </strong>
                      </span>
                      <div className="">
                        <div className="quantity-total__list-item">
                          {totalOrderLineItems?.map((orderLine: any, idx: number) => (
                            <div key={idx} className="quantity-total__item mr-4">
                              <span className="quantity-total__num mr-2">
                                {getDisplayNumberWithUnit(orderLine?.quantity ?? 0)}{' '}
                              </span>
                              <span className="mr-4">x</span>
                              {orderLine.variant_properties?.map((property: any, index: number) => (
                                <span key={index}>
                                  <span className="mr-4">
                                    <strong>{property.name}</strong>: {property.value}
                                  </span>
                                  {(orderLine.variant_properties ?? []).length - 1 != index && (
                                    <span className="mr-4"> | </span>
                                  )}
                                </span>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="is-flex">
                        <div className="is-flex text-large has-text-weight-medium content mt-3">
                          <img src={iconPath('shophelp_question_blue.svg')} alt="" />
                          <span className="ml-3 mr-3">Need help with your order?{'  '}</span>
                          <a className="is-link" onClick={() => redirectToSelectFlow('/order')}>
                            Contact us
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* list parcel info */}
                  <div className="list-parcel pt-5">
                    {orderDetail?.parcel_infos?.map((info, index) => (
                      <div key={index} className="parcel-item">
                        <PackageInfoTracking
                          index={orderDetail?.parcel_infos ? index + 1 : 0}
                          total={parcelsQuantity}
                          dataPackage={info}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <Header />
            <div className="page__content columns"></div>
          </>
        )}
      </div>

      <div className="title-order-container">
        <TitleSale className="title-sale-order" idScroll="order-tracking-catalog" />
      </div>

      <OrderTrackingSale version={versionCookie || ''} />

      <Footer />
      <style jsx>{style}</style>
      <style jsx global>{`
        .title-order-container {
          display: flex;
          justify-content: center;
          margin-top: 12px;

          .title-sale-order {
            max-width: 580px;
            margin: 0px;
          }

          @media screen and (min-width: 768px) {
            margin-top: 48px;
          }
        }
      `}</style>
    </div>
  ) : null
}

const style = css`
  .tracking-order-page {
    .loading-component {
      height: 100vh;
    }
    background-color: white;

    .quantity-total {
      .quantity-wrapper {
        &::after {
          width: 15px;
          height: 15px;
          content: '';
          border: 1px solid transparent;
          border-top-color: #e0e3e9;
          border-right-color: #e0e3e9;
          -webkit-transform: rotate(-45deg);
          -ms-transform: rotate(-45deg);
          transform: rotate(-45deg);
          top: 7px;
          left: 175px;
          background-color: #fff;
          position: relative;
          margin-top: 0;
          display: block;
        }
      }

      &__num {
        width: 19px;
        height: 24px;

        font-style: normal;
        font-weight: 600;
        font-size: 15px;
        line-height: 24px;
      }

      &__list-item {
        border: 1px solid #e0e3e9;
        box-shadow: 0px 3.2px 7.2px rgba(101, 115, 129, 0.13);
        border-radius: 6px;
        padding-top: 8px;
        padding-bottom: 8px;
        padding-left: 16px;
        padding-right: 16px;
      }

      &__item {
        padding-top: 5px;
        padding-bottom: 5px;
      }
    }

    .is-link {
      font-weight: 600;
      color: #0b92f1;
      cursor: pointer;
    }

    .parcel-item {
      border: 1px solid #e0e3e9;
      margin-bottom: 20px;
      box-shadow: 0px 3.2px 7.2px rgba(101, 115, 129, 0.13);
      border-radius: 6px;
    }

    @media screen and (min-width: 1200px) {
      .order {
        max-width: 450px;
        margin: 0 50px 0 auto;
      }

      .customer {
        max-width: 500px;
        margin: 0 auto;
      }

      .tracking {
        padding-top: 30px;
        max-width: 550px;
        margin: 0 auto 0 80px;
      }
    }

    @media screen and (max-width: 768px) {
      .order {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
      }

      .left-column {
        background-color: #fff;
      }

      .tracking {
        margin-top: -1rem;
      }
    }

    .order {
      padding-bottom: 100px;
      padding-top: 30px;
    }

    .customer {
      padding-top: 136px;
      //padding-bottom: 100px;

      &__title {
        font-weight: bold;
        font-size: 24px;
        line-height: 40px;
        margin-bottom: 32px;

        &--collapse {
          display: flex;
          align-items: center;
          padding: 8px 0 8px 15px;
          border: 1px solid #e8e8e8;
          margin-top: -17px;

          .title {
            &__text {
              color: var(--text);
              font-size: 14px;
              line-height: 32px;
              margin-left: 10px;
              margin-right: 10px;
              opacity: 0.5;
            }

            &__icon {
              img {
                height: 20px;
                width: 20px;
              }

              &--right {
                img {
                  height: 19px;
                  width: 14px;
                }
              }
            }
          }
        }
      }

      @media screen and (max-width: 768px) {
        padding-top: 0;

        &__title--collapse {
          margin-left: -0.75rem;
          margin-right: -0.75rem;
          margin-bottom: 16px;
        }
      }
    }

    .tracking__header {
      display: flex;

      &--status {
        color: #818181;
        line-height: 32px;
        margin-bottom: 32px;
      }
    }

    @media screen and (max-width: 768px) {
      .list-parcel {
        margin-bottom: 32px;
      }
    }
  }
`

export default TrackingsResult
